import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { urlFor } from "@/sanity/lib/image";
import { cn } from "@/lib/utils";
import { Button } from "./ui/button";

type Project = {
  _id: string;
  projectTitle: string;
  projectOverview?: string;
  featuredImage?: {
    asset: any;
    alt?: string;
  };
  client?: {
    name: string;
    industry?: {
      title: string;
    };
    logo?: {
      asset: any;
      alt?: string;
    };
  };
  services?: Array<{
    name: string;
  }>;
  tags?: Array<{
    name: string;
  }>;
  tools?: Array<{
    title: string;
  }>;
  projectUrl?: string;
  githubUrl?: string;
  featured?: boolean;
  order?: number;
};

type ProjectCardProps = {
  project: Project;
  className?: string;
  isFeatured?: boolean;
};

export function ProjectCard({
  project,
  className = "",
  isFeatured = false,
}: ProjectCardProps) {
  const {
    projectTitle,
    projectOverview = "",
    featuredImage,
    client,
    services,
    tags,
    tools,
    featured = false,
    projectUrl,
    githubUrl,
  } = project;

  // Safety function to check if an object is a Sanity reference
  const isReference = (obj: any): boolean => {
    return typeof obj === 'object' && obj && '_ref' in obj;
  };

  // Safe accessor for client industry
  const getClientIndustry = (): string => {
    // console.log('Client Debug:', { client, isReference: client ? isReference(client) : 'no client' });
    if (!client || isReference(client)) return "WEBSITE DESIGN";
    if (isReference(client.industry)) return "WEBSITE DESIGN"; // Added line
    // console.log('Client Industry:', client.industry);
    return client.industry?.title || "WEBSITE DESIGN";
  };

  // Safe accessor for service names
  const getServiceNames = (): string[] => {
    if (!services || !Array.isArray(services)) return ["WEB DESIGN"];
    return services
      .filter(service => service && !isReference(service))
      .map(service => service.name)
      .filter((name): name is string => typeof name === 'string' && name.trim() !== '');
  };

  return (
    <div
      className={cn(
        "w-full h-full bg-bg border border-border rounded-xl overflow-hidden flex flex-col",
        className
      )}
    >
      {/* Gradient background div */}
      <div
        className="absolute rounded-xl transition-opacity duration-300 opacity-0 group-hover:opacity-75"
        style={
          {
            inset: "-20px",
            background:
              "radial-gradient(at 15% 20%, hsla(24, 95%, 85%, 0.8) 0px, transparent 50%), radial-gradient(at 50% 20%, hsla(205, 95%, 85%, 0.7) 0px, transparent 50%), radial-gradient(at 85% 20%, hsla(160, 85%, 80%, 0.75) 0px, transparent 50%), radial-gradient(at 50% 70%, hsla(260, 95%, 88%, 0.7) 0px, transparent 50%)",
            filter: "blur(20px)",
            zIndex: -1,
          } as React.CSSProperties
        }
      />
      <div className="p-6 text-white flex flex-col h-full">
        <div className="space-y-4 flex-grow">
          {/* Client Industry */}
          <div className="flex items-center justify-between">
            <p className="text-sm uppercase tracking-widest text-subheading">
              {getClientIndustry()}
            </p>
            {(featured || isFeatured) && (
              <Badge
                variant="secondary"
                className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"
              >
                Featured
              </Badge>
            )}
          </div>

          {/* Project Title */}
          <h3 className="text-xl md:text-2xl text-heading">{projectTitle}</h3>

          {/* Project Overview */}
          <p className="text-base text-text">
            {projectOverview || "No description available."}
          </p>

          {/* Services */}
          {(() => {
            const serviceNames = getServiceNames();
            if (serviceNames.length > 0) {
              return (
                <div className="flex flex-wrap gap-2">
                  {serviceNames.map((serviceName, index) => (
                    <Badge
                      key={index}
                      variant="default"
                      className="text-xs font-medium text-coffee dark:text-coffee"
                    >
                      {serviceName}
                    </Badge>
                  ))}
                </div>
              );
            } else {
              return (
                <div className="flex flex-wrap gap-2">
                  <Badge
                    variant="secondary"
                    className="text-gray-800 dark:text-gray-800 border-border uppercase tracking-wider text-xs font-medium"
                  >
                    WEB DESIGN
                  </Badge>
                </div>
              );
            }
          })()}
        </div>

        {/* Featured Image */}
        <div className="mt-auto pt-6">
          {featuredImage ? (
            <div className="w-full h-48 rounded-lg overflow-hidden">
              <Image
                src={urlFor(featuredImage.asset).width(360).height(240).url()}
                alt={
                  featuredImage.alt ||
                  `Screenshot of the ${projectTitle} project`
                }
                width={360}
                height={240}
                sizes="(max-width: 768px) calc(100vw - 3rem), (max-width: 1200px) 50vw, 33vw"
                loading="lazy"
                className="object-cover w-full h-full object-top rounded-[10px] border-2 border-border"
              />
            </div>
          ) : (
            <div className="w-full h-48 bg-gray-800 rounded-lg flex items-center justify-center">
              <p className="text-gray-500 text-sm">No image available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
